<template>
  <Layout>
    <PageHeader :title="'Cart'" :items="breadcrumbs" />
    <div class="member-cart" :style="{ paddingBottom: '64px' }">

      <!-- Payment failed alert -->
      <b-alert v-if="paymentFailed" variant="danger" show dismissible @dismissed="paymentFailed = false">
        Payment failed. Please review your cart and try again.
      </b-alert>

      <!-- Stock error alerts -->
      <b-alert v-for="(err, idx) in stockErrors" :key="'stock-err-' + idx" variant="danger" show>
        {{ err.available > 0
          ? `${err.name}: only ${err.available} available (you requested ${err.requested})`
          : `${err.name} is no longer available` }}
      </b-alert>

      <!-- Empty state -->
      <div v-if="cartItems.length === 0 && !memberOrderingLoading" class="text-center py-5">
        <i class="bx bx-cart" style="font-size: 48px; color: var(--text-muted)"></i>
        <p class="text-muted mt-3">Your cart is empty</p>
        <b-button variant="primary" @click="handleNavigate('shop')">Browse Products</b-button>
      </div>

      <!-- Cart items -->
      <div v-else-if="cartItems.length > 0">
        <b-card
          v-for="item in cartItems"
          :key="item.product.id"
          class="cart-item-card mb-3"
          no-body
        >
          <div class="cart-item d-flex align-items-center p-3">
            <div class="cart-item-image mr-3">
              <img v-if="item.product.public_img_src || item.product.product_img_src"
                   :src="item.product.public_img_src || item.product.product_img_src"
                   :alt="item.product.name"
                   class="rounded" style="width: 60px; height: 60px; object-fit: cover;" />
              <div v-else class="product-image-placeholder rounded d-flex align-items-center justify-content-center"
                   style="width: 60px; height: 60px;">
                <i class="fas fa-cannabis"></i>
              </div>
            </div>
            <div class="cart-item-details flex-grow-1">
              <h4 class="cart-item-name mb-1">{{ item.product.name }}</h4>
              <span class="cart-item-price text-muted">R{{ formatPrice(item.product.price) }} each</span>
            </div>
            <div class="cart-item-qty d-flex align-items-center mr-3">
              <b-button size="sm" variant="outline-secondary" class="qty-btn"
                        @click="handleDecrement(item)" :disabled="isSubmitting"
                        aria-label="Decrease quantity">-</b-button>
              <span class="qty-display mx-2">{{ item.quantity }}</span>
              <b-button size="sm" variant="outline-secondary" class="qty-btn"
                        @click="handleIncrement(item)" :disabled="isSubmitting"
                        aria-label="Increase quantity">+</b-button>
            </div>
            <div class="cart-item-total text-right mr-3">
              <strong>R{{ formatPrice(item.product.price * item.quantity) }}</strong>
            </div>
            <b-button variant="link" class="text-danger p-0 remove-btn"
                      @click="handleRemove(item.product.id)" :disabled="isSubmitting"
                      aria-label="Remove item">
              <i class="bx bx-x" style="font-size: 20px;"></i>
            </b-button>
          </div>
        </b-card>

        <!-- Cart summary -->
        <b-card class="cart-summary mt-4">
          <div class="d-flex justify-content-between mb-2">
            <span>Subtotal</span>
            <span>R{{ formatPrice(cartTotal) }}</span>
          </div>
          <div class="d-flex justify-content-between mb-2 text-muted">
            <span>Shipping</span>
            <span>{{ cartTotal >= 1000 ? 'FREE' : shippingCostDisplay }}</span>
          </div>
          <hr />
          <div class="d-flex justify-content-between">
            <strong class="cart-total-label">Total</strong>
            <strong class="cart-total-amount">R{{ formatPrice(orderTotal) }}</strong>
          </div>
        </b-card>

        <!-- Checkout button -->
        <b-button
          variant="primary"
          block
          class="checkout-btn mt-4"
          :disabled="isSubmitting || cartItems.length === 0"
          @click="handleCheckout"
        >
          <b-spinner v-if="isSubmitting" small class="mr-2" />
          <span>{{ isSubmitting ? 'Processing...' : 'Pay & Place Order' }}</span>
        </b-button>
      </div>
    </div>

    <cart-bottom-nav
      active-tab="cart"
      :cart-count="cartItemCount"
      @navigate="handleNavigate"
    />
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import CartBottomNav from "@/components/member/cartBottomNav";
import { memberOrderingComputed, memberOrderingMethods } from "@/state/helpers";

export default {
  page: { title: "Cart" },
  components: { Layout, PageHeader, CartBottomNav },
  data() {
    return {
      stockErrors: [],
      paymentFailed: false,
      shippingCost: 0,
    };
  },
  computed: {
    ...memberOrderingComputed,
    breadcrumbs() {
      return [{ text: "Cart", active: true }];
    },
    orderTotal() {
      const shipping = this.cartTotal >= 1000 ? 0 : this.shippingCost;
      return this.cartTotal + shipping;
    },
    shippingCostDisplay() {
      return this.shippingCost > 0 ? `R${this.formatPrice(this.shippingCost)}` : "FREE";
    },
  },
  methods: {
    ...memberOrderingMethods,
    formatPrice(val) {
      return parseFloat(val).toFixed(2);
    },
    handleIncrement(item) {
      this.updateCartQuantity({ productId: item.product.id, quantity: item.quantity + 1 });
    },
    handleDecrement(item) {
      if (item.quantity > 1) {
        this.updateCartQuantity({ productId: item.product.id, quantity: item.quantity - 1 });
      }
      // At qty 1, do nothing — use remove button
    },
    handleRemove(productId) {
      this.removeFromCart(productId);
    },
    handleNavigate(tab) {
      const routes = {
        shop: "member-shop",
        cart: "member-cart",
        orders: "member-orders",
        profile: "member-profile",
      };
      if (routes[tab] && this.$route.name !== routes[tab]) {
        this.$router.push({ name: routes[tab] });
      }
    },
    async handleCheckout() {
      this.stockErrors = [];
      try {
        const response = await this.submitCheckout();
        if (response && response.payment_url) {
          window.location.href = response.payment_url;
        }
      } catch (error) {
        if (error.response && error.response.status === 422 && error.response.data.details) {
          this.stockErrors = error.response.data.details;
          // Auto-adjust cart based on stock failures
          error.response.data.details.forEach(detail => {
            if (detail.available <= 0) {
              this.removeFromCart(detail.product_id);
            } else if (detail.available < detail.requested) {
              this.updateCartQuantity({
                productId: detail.product_id,
                quantity: detail.available,
              });
            }
          });
        }
        // Generic errors handled by Vuex error state
      }
    },
  },
  created() {
    // Check for payment failure redirect
    if (this.$route.query.payment === "failed") {
      this.paymentFailed = true;
    }
  },
};
</script>

<style scoped lang="scss">
@import "~@/design/tokens/brand";
@import "~@/design/tokens/typography";
@import "~@/design/tokens/spacing";
@import "~@/design/tokens/surfaces";
@import "~@/design/tokens/feedback";

.member-cart {
  animation: member-fade-in 150ms ease;
}

@keyframes member-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

.cart-item-card {
  background: $surface-card;
}

.cart-item-name {
  font-size: $type-body-lg;
  font-weight: 600;
  margin: 0;
}

.cart-item-price {
  font-size: $type-small;
}

.qty-btn {
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qty-display {
  min-width: 24px;
  text-align: center;
  font-weight: 600;
}

.remove-btn {
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cart-total-amount {
  font-size: 24px;
  color: $text-primary;
}

.checkout-btn {
  min-height: 44px;
  font-weight: 600;
}

.product-image-placeholder {
  background: $surface-elevated;
  color: $text-muted;
  font-size: 24px;
}
</style>
